import BaseCheckbox from 'components/atoms/base-checkbox';
import React from 'react';
import BaseInputNumber from 'components/atoms/base-input-number';
import { Radio } from 'antd';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseInput from 'components/atoms/base-input';
import BaseDatePicker from 'components/atoms/datePicker';

import s from './styles.module.scss';
import FormItem from '../form-item';

import type { FormikProps } from 'formik';
import type { FieldValueMap, TFieldProps, TFormTemplateItem } from '../types';

const resetSubItems = (
  subItems: TFormTemplateItem[],
  formik: FormikProps<any>,
) => {
  subItems?.forEach((subItem) => {
    if (subItem.type === 'info') return;

    if (
      subItem.type === 'input' ||
      subItem.type === 'input-number' ||
      subItem.type === 'input-date' ||
      subItem.type === 'textarea' ||
      subItem.type === 'radio'
    ) {
      formik.setFieldValue(subItem.name, undefined);
    }

    if (subItem.type === 'checkbox') {
      formik.setFieldValue(subItem.name, []);
    }

    // TFormTemplateItem → items
    if (subItem.items?.length) {
      resetSubItems(subItem.items, formik);
    }

    // checkbox / radio → options → subItems
    subItem.options?.forEach((option) => {
      if (option.subItems?.length) {
        resetSubItems(option.subItems, formik);
      }
    });
  });
};

export const fieldTypeComponents: {
  [K in keyof FieldValueMap]: (props: TFieldProps<K>) => React.ReactNode;
} = {
  empty: () => null,
  checkbox: ({ item, onChange, value = [], formik }) => {
    return item?.options?.map((option) => {
      const checkboxValue = value.includes(option.value);

      if (option?.type === 'info') {
        return (
          <div key={option.key}>
            <p className={s['info-option']}>{option.label}</p>
            {option?.subItems &&
              option?.subItems.map((i) => (
                <FormItem item={i} key={i.key} formik={formik} />
              ))}
          </div>
        );
      }

      return (
        <React.Fragment key={option.key}>
          <BaseCheckbox
            placeholder={option.label}
            value={checkboxValue}
            onChange={(v) => {
              let newValues = value;
              if (v) newValues.push(option.value);
              else newValues = newValues.filter((val) => val !== option.value);

              const newCheckboxValue = newValues.includes(option.value);

              onChange(newValues);

              if (!newCheckboxValue && option.subItems) {
                resetSubItems(option.subItems, formik);
              }
            }}
          />
          {option?.subItems?.map((subItem) => {
            const isMoveRight = [
              'input',
              'input-number',
              'input-date',
              'checkbox',
              'radio',
              'textarea',
            ].includes(subItem.type);
            const isCurrentOptionSelected = value.includes(option.value);

            if (
              option?.hideSubitemsIfParentNotSelected &&
              !isCurrentOptionSelected
            ) {
              return null;
            }
            const paddingLeft = isMoveRight ? 20 : 0;

            return (
              <div style={{ paddingLeft }} key={subItem.key}>
                <FormItem item={subItem} key={subItem.key} formik={formik} />
              </div>
            );
          })}
        </React.Fragment>
      );
    });
  },
  radio: ({ item, onChange, value, formik }) => (
    <Radio.Group value={value} className={s['radio-items']} key={item.key}>
      {item?.options?.map((option) => {
        if (option?.type === 'info') {
          return (
            <div key={option.key}>
              <p className={s['info-option']}>{option.label}</p>
              {option?.subItems &&
                option?.subItems.map((i) => (
                  <FormItem item={i} key={i.key} formik={formik} />
                ))}
            </div>
          );
        }

        return (
          <React.Fragment key={option.key}>
            <Radio
              value={option.value}
              onChange={(e) => {
                onChange(e.target.value);

                const otherItemOptions = item?.options?.filter(
                  (o) => o.value !== option.value,
                );
                otherItemOptions?.forEach((o) => {
                  if (o.subItems) resetSubItems(o.subItems, formik);
                });

                if (option.subItems) {
                  resetSubItems(option.subItems, formik);
                }
              }}
            >
              <span style={{ cursor: 'pointer' }}>{option.label}</span>
            </Radio>
            {option?.subItems?.map((subItem) => {
              const isMoveRight = [
                'input',
                'input-number',
                'checkbox',
                'radio',
                'textarea',
              ].includes(subItem.type);
              const isCurrentOptionSelected = value === option.value;

              if (
                option?.hideSubitemsIfParentNotSelected &&
                !isCurrentOptionSelected
              ) {
                return null;
              }
              const paddingLeft = isMoveRight ? 20 : 0;

              return (
                <div style={{ paddingLeft }} key={subItem.key}>
                  <FormItem item={subItem} key={subItem.key} formik={formik} />
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </Radio.Group>
  ),
  textarea: ({ onChange, value, item }) => (
    <BaseTextArea
      rows={6}
      placeholder={item.placeholder ?? 'Type here details'}
      onChange={(v) => onChange(v)}
      value={value}
      key={item.key}
    />
  ),
  input: ({ onChange, value, item }) => (
    <BaseInput
      value={value}
      onChange={onChange}
      key={item.key}
      placeholder={item.placeholder}
    />
  ),
  'input-number': ({ onChange, value, item }) => {
    const validation = item.validation ?? {};
    const { min, max } = validation;
    return (
      <BaseInputNumber
        placeholder="0.0"
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        key={item.key}
      />
    );
  },
  'input-date': ({ onChange, value, item }) => {
    return <BaseDatePicker value={value} onChange={onChange} key={item.key} />;
  },
};
